import React, { useState, useEffect, useMemo } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";
import { customers as customersAPI } from "../../../lib/database";
import { useToast } from "../../../components/ui/Toast";

// Dynamic import for qrcode library
let QRCode = null;
const loadQRCode = async () => {
  if (!QRCode) {
    try {
      QRCode = (await import("qrcode")).default;
    } catch (error) {
      console.error("Failed to load qrcode library:", error);
      throw new Error(
        "QR code library not available. Please install: npm install qrcode"
      );
    }
  }
  return QRCode;
};

const GenerateQRModal = ({
  isOpen,
  onClose,
  customers: allCustomers,
  clusters,
  regions,
  teams = [],
  onSuccess,
}) => {
  const { showToast } = useToast();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [filters, setFilters] = useState({
    region: "all",
    cluster: "all",
    team: "all",
  });
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (isOpen && allCustomers) {
      // Filter out customers who already have a QR code
      const customersWithoutQR = allCustomers.filter(
        (customer) => !customer.qr_code
      );
      setCustomers(customersWithoutQR);
    }
  }, [isOpen, allCustomers]);

  // Filter customers based on filters and search
  useEffect(() => {
    let result = [...customers];

    // Apply filters
    if (filters.region !== "all") {
      result = result.filter((c) => c.region_id === filters.region);
    }
    if (filters.cluster !== "all") {
      result = result.filter((c) => c.cluster_id === filters.cluster);
    }
    if (filters.team !== "all") {
      if (filters.team === "not_assigned") {
        result = result.filter((c) => !c.team_id);
      } else {
        result = result.filter((c) => c.team_id === filters.team);
      }
    }

    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          c.name?.toLowerCase().includes(searchLower) ||
          c.cin?.toLowerCase().includes(searchLower) ||
          c.usn?.toLowerCase().includes(searchLower) ||
          c.phone_number?.includes(searchTerm)
      );
    }

    setFilteredCustomers(result);
  }, [customers, filters, searchTerm]);

  const regionOptions = useMemo(() => {
    return [
      { value: "all", label: "All Regions" },
      ...(regions?.map((r) => ({ value: r.id, label: r.name })) || []),
    ];
  }, [regions]);

  const clusterOptions = useMemo(() => {
    return [
      { value: "all", label: "All Clusters" },
      ...(clusters?.map((c) => ({ value: c.id, label: c.name })) || []),
    ];
  }, [clusters]);

  const teamOptions = useMemo(() => {
    return [
      { value: "all", label: "All Teams" },
      { value: "not_assigned", label: "Not Assigned" },
      ...(teams?.map((t) => ({ value: t.id, label: t.name })) || []),
    ];
  }, [teams]);

  const handleSelectAll = () => {
    if (selectedCustomerIds.length === filteredCustomers.length) {
      setSelectedCustomerIds([]);
    } else {
      setSelectedCustomerIds(filteredCustomers.map((c) => c.id));
    }
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomerIds((prev) => {
      if (prev.includes(customerId)) {
        return prev.filter((id) => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  const generateQRCode = async (serviceNumber) => {
    try {
      await loadQRCode();
      // Generate QR code as data URL encoding the service number
      const qrDataUrl = await QRCode.toDataURL(serviceNumber, {
        width: 300,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      });
      return qrDataUrl;
    } catch (error) {
      console.error("Error generating QR code:", error);
      throw error;
    }
  };

  const handleGenerateQR = async () => {
    if (selectedCustomerIds.length === 0) {
      showToast("Please select at least one customer", "error");
      return;
    }

    try {
      setGenerating(true);

      // Get selected customers
      const selectedCustomers = filteredCustomers.filter((c) =>
        selectedCustomerIds.includes(c.id)
      );

      // Check if all selected customers have CIN
      const customersWithoutCIN = selectedCustomers.filter((c) => !c.cin);
      if (customersWithoutCIN.length > 0) {
        showToast(
          "Some selected customers don't have a CIN. Please ensure all customers have a Customer ID.",
          "error"
        );
        setGenerating(false);
        return;
      }

      // Generate QR codes for each selected customer
      const updatePromises = selectedCustomers.map(async (customer) => {
        try {
          // Generate QR code encoding the CIN
          const qrDataUrl = await generateQRCode(customer.cin);

          // Update customer with QR code
          await customersAPI.update(customer.id, {
            qr_code: qrDataUrl, // Store as data URL
          });

          return { success: true, customerId: customer.id, cin: customer.cin };
        } catch (error) {
          console.error(
            `Error generating QR for customer ${customer.id}:`,
            error
          );
          return {
            success: false,
            customerId: customer.id,
            cin: customer.cin,
            error: error.message,
          };
        }
      });

      const results = await Promise.all(updatePromises);
      const successCount = results.filter((r) => r.success).length;
      const failCount = results.filter((r) => !r.success).length;

      if (successCount > 0) {
        showToast(
          `Successfully generated QR codes for ${successCount} customer(s)${
            failCount > 0 ? `. ${failCount} failed.` : ""
          }`,
          "success"
        );
        if (onSuccess) {
          onSuccess();
        }
        onClose();
        setSelectedCustomerIds([]);
      } else {
        showToast(
          "Failed to generate QR codes for all selected customers",
          "error"
        );
      }
    } catch (error) {
      console.error("Error generating QR codes:", error);
      showToast(
        error.message || "Failed to generate QR codes. Please try again.",
        "error"
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    setSelectedCustomerIds([]);
    setFilters({ region: "all", cluster: "all", team: "all" });
    setSearchTerm("");
    onClose();
  };

  if (!isOpen) return null;

  const allSelected =
    filteredCustomers.length > 0 &&
    selectedCustomerIds.length === filteredCustomers.length;
  const someSelected =
    selectedCustomerIds.length > 0 &&
    selectedCustomerIds.length < filteredCustomers.length;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-6xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-semibold text-foreground">
              Generate QR Codes
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Select customers to generate unique QR codes (encoding service
              number)
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={handleClose}
          />
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-border space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Search
              </label>
              <input
                type="text"
                placeholder="Search by name, USN, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Region
              </label>
              <Select
                value={filters.region}
                onChange={(value) => setFilters({ ...filters, region: value })}
                options={regionOptions}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Cluster
              </label>
              <Select
                value={filters.cluster}
                onChange={(value) => setFilters({ ...filters, cluster: value })}
                options={clusterOptions}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Team
              </label>
              <Select
                value={filters.team}
                onChange={(value) => setFilters({ ...filters, team: value })}
                options={teamOptions}
              />
            </div>
          </div>
        </div>

        {/* Customer List */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredCustomers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                No customers found matching the filters.
              </p>
            </div>
          ) : (
            <>
              {/* Select All */}
              <div className="mb-4 pb-4 border-b border-border">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAll}
                  label={`Select All (${filteredCustomers.length} customers)`}
                />
              </div>

              {/* Customer Table */}
              <div className="space-y-2">
                {filteredCustomers.map((customer) => {
                  const isSelected = selectedCustomerIds.includes(customer.id);
                  const hasQRCode = !!customer.qr_code;
                  const regionName = customer.regions?.name || "N/A";
                  const clusterName = customer.clusters?.name || "N/A";
                  const teamName =
                    teams.find((t) => t.id === customer.team_id)?.name || "N/A";

                  return (
                    <div
                      key={customer.id}
                      className={`flex items-center gap-4 p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors ${
                        isSelected ? "bg-primary/10 border-primary" : ""
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelectCustomer(customer.id)}
                      />
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-4">
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {customer.cin || "N/A"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            CIN
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {customer.name || "N/A"}
                          </p>
                          <p className="text-xs text-muted-foreground">Name</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {regionName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Region
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {clusterName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Cluster
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {teamName}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Team
                            </p>
                          </div>
                          {hasQRCode && (
                            <span className="ml-auto px-2 py-1 text-xs bg-success/10 text-success rounded">
                              QR Exists
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {selectedCustomerIds.length > 0
              ? `${selectedCustomerIds.length} customer(s) selected`
              : "No customers selected"}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={generating}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              iconName="QrCode"
              iconPosition="left"
              onClick={handleGenerateQR}
              loading={generating}
              disabled={selectedCustomerIds.length === 0 || generating}
            >
              Generate QR Codes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateQRModal;
